import { Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import { useConductAnalysisData, type ConductData } from "src-next/modules/clinical-insights/hooks/useConductAnalysisData";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

const texts = getInsightAnalysisTexts("condutas");

interface PatientConductGroup {
  patientId: string;
  patientName: string;
  totalConducts: number;
  conducts: ConductData[];
  statusCounts: Record<string, number>;
}

export function ConductAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
  fetchWarm,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const author = `${oids.cnpj}-${organizationId}`;
  const practitionerFilter = practitionerId || "all";

  const getPractitionerName = useCallback(
    (practitionerReference: string) => {
      if (!practitionerReference) return "Não informado";
      const practId = practitionerReference.split("/")[1];
      const pract = practitioner?.find((p) => p.resource?.id === practId);
      const displayName =
        pract?.resource?.name?.[0]?.text ||
        `${pract?.resource?.name?.[0]?.given?.join(" ") || ""} ${
          pract?.resource?.name?.[0]?.family || ""
        }`.trim();
      return displayName || practId;
    },
    [practitioner]
  );

  const {
    conducts,
    patients,
    loadPhase,
    patientsProgress,
    hasData,
    retry,
  } = useConductAnalysisData({
    author,
    practitionerFilter,
    dateRange,
    getPractitionerName,
    onLoadPhaseChange,
    fetchEnabled: fetchWarm ?? isActive,
    isActive,
  });

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;

  const patientGroups = useMemo(() => {
    const groups = new Map<string, PatientConductGroup>();
    for (const conduct of conducts) {
      const patientId = conduct.patientId.split("/")[1];
      const patient = patients.get(patientId);
      const patientName =
        patient?.name?.[0]?.text ||
        `${patient?.name?.[0]?.given?.join(" ") || ""} ${
          patient?.name?.[0]?.family || ""
        }`.trim() ||
        "Não informado";

      if (!groups.has(patientId)) {
        groups.set(patientId, {
          patientId,
          patientName,
          totalConducts: 0,
          conducts: [],
          statusCounts: {},
        });
      }
      const group = groups.get(patientId)!;
      group.totalConducts++;
      group.conducts.push({ ...conduct, patientName });
      const status = conduct.status || "aberta";
      group.statusCounts[status] = (group.statusCounts[status] ?? 0) + 1;
    }
    return Array.from(groups.values()).sort((a, b) => b.totalConducts - a.totalConducts);
  }, [conducts, patients]);

  const { conductStats, totalConducts } = useMemo(() => {
    const statusMap = new Map<string, number>();
    for (const conduct of conducts) {
      const status = conduct.status || "aberta";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    }
    const total = conducts.length;
    const stats = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
    return { conductStats: stats, totalConducts: total };
  }, [conducts]);

  const allConductsData = useMemo(() => {
    const conductMap = new Map<string, number>();
    for (const conduct of conducts) {
      conductMap.set(conduct.conduct, (conductMap.get(conduct.conduct) || 0) + 1);
    }
    return Array.from(conductMap.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [conducts]);

  const statusTranslation: Record<string, string> = {
    realizada: "Realizada",
    aberta: "Em Aberto",
    parcial: "Parcialmente",
    "não realizada": "Não Realizada",
  };

  const statusItems: InsightChartItem[] = useMemo(
    () =>
      conductStats.map((stat) => ({
        key: stat.status,
      label:
        statusTranslation[stat.status] ||
        stat.status.charAt(0).toUpperCase() + stat.status.slice(1),
        value: stat.count,
      })),
    [conductStats]
  );

  const rankingItems: InsightChartItem[] = useMemo(
    () =>
      allConductsData.map((item) => ({
        key: item.label,
        label: item.label,
        value: item.value,
      })),
    [allConductsData]
  );

  const patientRows: InsightPatientRow[] = useMemo(
    () =>
      patientGroups.map((group) => ({
        patientId: group.patientId,
      patientName: group.patientName,
        patient: patients.get(group.patientId),
        items: group.conducts.map((conduct) => {
          const statusKey = conduct.status || "aberta";
          return {
            itemKey: conduct.conduct,
            label: conduct.conduct,
            statusKey,
            statusLabel:
              statusTranslation[statusKey] ||
              statusKey.charAt(0).toUpperCase() + statusKey.slice(1),
            practitioner: conduct.practitioner,
            date: conduct.date,
          };
        }),
      })),
    [patientGroups, patients]
  );

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage =
      totalConducts > 0 ? Math.round((sorted[0].value / totalConducts) * 100) : 0;
    return `${percentage}% das ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, totalConducts, texts.itemPlural]);

  const emptyPeriodLabel =
    periodDisplayLabel ??
    (dateRange?.startDate
      ? dayjs(dateRange.startDate).format("MMMM [de] YYYY")
      : "este período");

  const emptyPractitionerLabel = useMemo(() => {
    if (practitionerFilter === "all") return "todos os profissionais";
    const entry = practitioner?.find((p) => p.resource?.id === practitionerFilter);
    const name = entry?.resource?.name?.[0]?.text;
    if (name) return name;
    return "este profissional";
  }, [practitioner, practitionerFilter]);

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#9c27b0"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={patientsProgress}
        hasData={hasData}
        isEmpty={totalConducts === 0}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={retry}
        statusChart={{
          insightText: statusInsightTitle,
          items: statusItems,
        }}
        rankingChart={{
          insightText: texts.rankingHint,
          items: rankingItems,
        }}
        patientRows={patientRows}
        statusLabelByKey={statusTranslation}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
